import { Fragment, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { MoreHoriz, NavArrowRight } from 'iconoir-react';
import { MoreVertical } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  dropdownMenuItemDestructiveClass,
} from 'components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { cn } from 'lib/utils';
import { useIsCompactSidebar } from 'hooks/use-mobile';
import { useTheme } from 'components/core/theme-provider';
import { modalOption } from '/models/sidebar';
import { ConversationMenuIcon } from './conversation-menu-icon';

const destructiveOptionIds = new Set(['delete', 'leave']);

const SIDEBAR_MENU_WIDTH = 250;
const SIDEBAR_MENU_GAP = 4;
const SIDEBAR_MENU_MARGIN = 8;

// Above DialogContent (z-[110]) so the menu works inside the chat history search dialog.
const pageMenuLayerClass = 'z-[140]';

interface ConversationContextMenuProps {
  options: modalOption[];
  contentSide?: 'top' | 'right' | 'bottom' | 'left';
  contentAlign?: 'start' | 'center' | 'end';
  variant?: 'sidebar' | 'page';
  triggerClassName?: string;
  triggerAriaLabel?: string;
}

export function getSidebarConversationMenuPosition(
  trigger: Pick<DOMRect, 'right' | 'bottom' | 'top'>,
  menu: { width: number; height: number },
  viewport: { width: number; height: number },
  preferBelow = false,
) {
  let left = trigger.right + SIDEBAR_MENU_GAP;
  const maxLeft = viewport.width - menu.width - SIDEBAR_MENU_MARGIN;
  if (left > maxLeft) {
    left = Math.max(SIDEBAR_MENU_MARGIN, maxLeft);
  }

  if (preferBelow) {
    let top = trigger.bottom + SIDEBAR_MENU_GAP;
    const overflowBottom = top + menu.height > viewport.height - SIDEBAR_MENU_MARGIN;
    if (overflowBottom) {
      top = Math.max(SIDEBAR_MENU_MARGIN, trigger.top - menu.height - SIDEBAR_MENU_GAP);
    }
    return { top, left };
  }

  let top = trigger.bottom - menu.height;
  const maxTop = viewport.height - menu.height - SIDEBAR_MENU_MARGIN;
  if (top > maxTop) {
    top = Math.max(SIDEBAR_MENU_MARGIN, maxTop);
  }
  if (top < SIDEBAR_MENU_MARGIN) {
    top = SIDEBAR_MENU_MARGIN;
  }

  return { top, left };
}

function pinSidebarMenuToTrigger(
  menuNode: HTMLElement,
  triggerNode: HTMLElement,
  preferBelow = false,
) {
  const wrapper = menuNode.parentElement;
  if (!wrapper) return;

  const menuHeight = Math.max(menuNode.offsetHeight, wrapper.offsetHeight, 1);
  const { top, left } = getSidebarConversationMenuPosition(
    triggerNode.getBoundingClientRect(),
    { width: SIDEBAR_MENU_WIDTH, height: menuHeight },
    { width: window.innerWidth, height: window.innerHeight },
    preferBelow,
  );

  wrapper.style.setProperty('position', 'fixed', 'important');
  wrapper.style.setProperty('left', `${left}px`, 'important');
  wrapper.style.setProperty('top', `${top}px`, 'important');
  wrapper.style.setProperty('right', 'auto', 'important');
  wrapper.style.setProperty('bottom', 'auto', 'important');
  wrapper.style.setProperty('transform', 'none', 'important');
  wrapper.style.setProperty('max-width', 'none', 'important');
  wrapper.style.setProperty('min-width', `${SIDEBAR_MENU_WIDTH}px`, 'important');
  wrapper.style.setProperty('width', `${SIDEBAR_MENU_WIDTH}px`, 'important');
  wrapper.style.setProperty('overflow', 'visible', 'important');
  wrapper.style.setProperty('z-index', '200', 'important');

  menuNode.style.setProperty('width', `${SIDEBAR_MENU_WIDTH}px`, 'important');
  menuNode.style.setProperty('min-width', `${SIDEBAR_MENU_WIDTH}px`, 'important');
  menuNode.style.setProperty('max-width', 'none', 'important');
  menuNode.style.setProperty('overflow', 'visible', 'important');
}

export function ConversationContextMenu({
  options,
  contentSide = 'right',
  contentAlign = 'end',
  variant = 'sidebar',
  triggerClassName,
  triggerAriaLabel,
}: ConversationContextMenuProps) {
  const { t } = useTranslation();
  const isCompact = useIsCompactSidebar();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [open, setOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const menuLabel = triggerAriaLabel ?? t('MORE_OPTIONS', { defaultValue: 'More options' });
  const [expandedSubmenuId, setExpandedSubmenuId] = useState<string | null>(null);
  const firstDestructiveIndex = options.findIndex((o) => destructiveOptionIds.has(o.id));

  const isPageVariant = variant === 'page';
  const isSidebarVariant = variant === 'sidebar';
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLElement | null>(null);

  const pinSidebarMenu = useCallback(() => {
    if (!menuRef.current || !triggerRef.current) return;
    pinSidebarMenuToTrigger(menuRef.current, triggerRef.current, isCompact);
  }, [isCompact]);

  useLayoutEffect(() => {
    if (!open || !isSidebarVariant) return;
    pinSidebarMenu();
    const frame = requestAnimationFrame(pinSidebarMenu);
    window.addEventListener('resize', pinSidebarMenu);
    window.addEventListener('scroll', pinSidebarMenu, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', pinSidebarMenu);
      window.removeEventListener('scroll', pinSidebarMenu, true);
    };
  }, [open, isSidebarVariant, pinSidebarMenu, expandedSubmenuId]);

  const menuItems = options.map((option, index) => {
    const isDestructive = destructiveOptionIds.has(option.id);
    const showSeparator =
      isDestructive && index === firstDestructiveIndex && firstDestructiveIndex > 0;

    if (option.type === 'submenu') {
      if (isCompact) {
        const isExpanded = expandedSubmenuId === option.id;
        return (
          <Fragment key={option.id}>
            <DropdownMenuItem
              disabled={option.isDisabled}
              onSelect={(event) => {
                event.preventDefault();
                setExpandedSubmenuId(isExpanded ? null : option.id);
              }}
              onClick={(event) => event.stopPropagation()}
            >
              <ConversationMenuIcon optionId={option.id} />
              <span>{option.name}</span>
              <NavArrowRight
                width={16}
                height={16}
                strokeWidth={1.5}
                className={cn('ml-auto size-4 shrink-0', isExpanded && 'rotate-90')}
              />
            </DropdownMenuItem>
            {isExpanded &&
              option.subOptions?.map((subOption) => (
                <DropdownMenuItem
                  key={subOption.id}
                  className="pl-8"
                  onClick={(event) => {
                    event.stopPropagation();
                    if (!subOption?.isDisabled && subOption?.callback) {
                      subOption.callback();
                    }
                  }}
                  disabled={subOption?.isDisabled}
                >
                  <ConversationMenuIcon optionId={subOption.id} />
                  <span>{subOption.name}</span>
                </DropdownMenuItem>
              ))}
          </Fragment>
        );
      }

      return (
        <DropdownMenuSub key={option.id}>
          <DropdownMenuSubTrigger
            onClick={(e) => e.stopPropagation()}
            disabled={option.isDisabled}
          >
            <ConversationMenuIcon optionId={option.id} />
            <span>{option.name}</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent
            avoidCollisions={!isSidebarVariant}
            className={cn(isPageVariant && pageMenuLayerClass)}
          >
            {option.subOptions?.map((subOption) => (
              <DropdownMenuItem
                key={subOption.id}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!subOption?.isDisabled && subOption?.callback) {
                    subOption.callback();
                  }
                }}
                disabled={subOption?.isDisabled}
              >
                <ConversationMenuIcon optionId={subOption.id} />
                <span>{subOption.name}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      );
    }

    return (
      <Fragment key={option.id}>
        {showSeparator && <DropdownMenuSeparator />}
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            option.callback?.();
          }}
          disabled={option.isDisabled}
          className={cn(isDestructive && dropdownMenuItemDestructiveClass)}
        >
          <ConversationMenuIcon optionId={option.id} />
          <span>{option.name}</span>
        </DropdownMenuItem>
      </Fragment>
    );
  });

  return (
    <TooltipProvider delayDuration={300}>
      <DropdownMenu
        modal={!isSidebarVariant}
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) setExpandedSubmenuId(null);
        }}
      >
        <Tooltip open={!open && tooltipOpen} onOpenChange={setTooltipOpen}>
          <DropdownMenuTrigger asChild>
            <TooltipTrigger asChild>
              <button
                ref={triggerRef}
                type="button"
                className={cn(
                  isPageVariant
                    ? cn('relative size-6 shrink-0 hover:opacity-80', isDark ? 'text-foreground' : 'text-grey-700')
                    : 'ml-1 rounded-md p-1 hover:bg-muted-foreground/10',
                  triggerClassName
                )}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                aria-label={menuLabel}
              >
                {isPageVariant ? (
                  <MoreVertical className="size-6" strokeWidth={1.75} />
                ) : (
                  <MoreHoriz
                    width={18}
                    height={18}
                    strokeWidth={1.5}
                    className={cn(
                      'text-sidebar-foreground',
                      isDark ? 'group-hover:!text-true-white' : 'group-hover:text-grey-900'
                    )}
                  />
                )}
              </button>
            </TooltipTrigger>
          </DropdownMenuTrigger>
          <TooltipContent
            side="top"
            sideOffset={6}
            className={cn(
              'rounded-nav border border-grey-100 bg-true-white px-3 py-1.5 text-sm font-normal leading-heading shadow-primary-soft dark:border-sidebar-border dark:bg-sidebar',
              isDark ? 'text-true-white' : 'text-grey-900'
            )}
          >
            {menuLabel}
          </TooltipContent>
        </Tooltip>
        <DropdownMenuContent
          side={isSidebarVariant ? 'right' : contentSide}
          align={isSidebarVariant ? 'end' : contentAlign}
          sideOffset={4}
          avoidCollisions={!isSidebarVariant}
          container={isSidebarVariant ? document.body : undefined}
          data-conversation-sidebar-menu={isSidebarVariant ? '' : undefined}
          className={
            isSidebarVariant ? 'z-[200] w-[250px] min-w-[250px] max-w-[250px] overflow-visible' : pageMenuLayerClass
          }
          style={isSidebarVariant ? { width: 250, minWidth: 250, maxWidth: 250 } : undefined}
          ref={
            isSidebarVariant
              ? (node) => {
                  menuRef.current = node;
                  if (node && triggerRef.current) {
                    pinSidebarMenuToTrigger(node, triggerRef.current, isCompact);
                  }
                }
              : undefined
          }
        >
          {menuItems}
        </DropdownMenuContent>
      </DropdownMenu>
    </TooltipProvider>
  );
}
